"use client";

import { FC, HTMLAttributes, ReactNode } from "react";
import clsx from "clsx";
import Image from "next/image";

import {
  Accordion,
  AccordionItem,
  AccordionControl,
  AccordionPanel,
} from "@mantine/core";

import HtmlText from "./HtmlText";
import SectionHeader from "./SectionHeader";
import PlusIcon from "@/components/icons/PlusIcon";
import { FAQPage, WithContext } from "schema-dts";
import { env } from "@/env.mjs";
import { usePathname } from "next/navigation";
import { slugify } from "@/utils/slugify.helper";
import { extractText } from "@/utils/extract-text.helper";

// -------------------------------------------------------------------------------------------

export type IQuestion = {
  heading: string;
  description?: ReactNode;
};

export type IFaqSectionProps = HTMLAttributes<HTMLElement> & {
  heading?: string;
  questions?: IQuestion[];
};

const FaqSection: FC<IFaqSectionProps> = (props) => {
  const { heading, questions, className, ...other } = props;
  const pathname = usePathname();
  const updatedSiteUrl = env.NEXT_PUBLIC_SITE_URL.replace(/\/+$/, "");

  const jsonLd: WithContext<FAQPage> = {
    "@type": "FAQPage",
    "@context": "https://schema.org",
    "@id": `${updatedSiteUrl}${pathname}#faq`,
    mainEntity: questions?.map((question) => ({
      "@type": "Question",
      "@id": `${env.NEXT_PUBLIC_SITE_URL.trimEnd()}${pathname}#faq-${slugify(question.heading)}`,
      name: question.heading,
      acceptedAnswer: {
        "@type": "Answer",
        text: extractText(question.description),
      },
    })),
  };

  return (
    <section
      id="faq"
      className={clsx(
        "container mx-auto py-10 md:py-14 lg:py-16 xl:py-20",
        className,
      )}
      {...other}
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <div className="mx-auto w-full max-w-[994px]">
        {heading && (
          <SectionHeader
            eyebrow="FAQs"
            title={heading}
            className="mb-8 md:mb-10 lg:mb-12"
          />
        )}

        {questions && questions.length > 0 && (
          <Accordion
            variant="separated"
            classNames={{
              label: "py-2 dark:text-white lg:py-4 xl:py-6",
              chevron: "size-5 data-[rotate]:rotate-45",
              item: "!mt-0 border-b border-b-[#D9D9D9] last:!border-x-0 last:!border-b-0 last:border-t-0",
            }}
            chevron={
              <div className="h-4 w-4 lg:h-5 lg:w-5">
                <PlusIcon />
              </div>
            }
            defaultValue={
              questions[0] ? slugify(questions[0].heading) : undefined
            }
          >
            {questions.map((question, idx) => (
              <AccordionItem
                key={idx}
                value={slugify(question.heading)}
                id={`faq-${slugify(question.heading)}`}
                className="rounded-none border-x-0 border-t-0 bg-white px-4 dark:bg-black-900 md:px-8 lg:px-12 xl:px-16"
              >
                <AccordionControl
                  classNames={{
                    // Canonical Bricolage Grotesque + clamp scale.
                    // Matches the card heading scale used by SolutionCard
                    // / IndustryCard so the FAQ questions read in the
                    // same type family as the rest of the home page.
                    label:
                      "font-bricolage-grotesque text-[clamp(1rem,0.5vw+0.875rem,1.25rem)] font-medium text-black-900 dark:text-white",
                  }}
                  className="px-0"
                >
                  {question.heading}
                </AccordionControl>

                {question.description && (
                  <AccordionPanel
                    classNames={{
                      content:
                        "p-0 pb-5 pt-3 dark:text-black-200 md:pt-4 lg:pb-6",
                    }}
                    className="p-0 pr-4 md:pr-10 lg:pr-20"
                  >
                    {/* Answer body — canonical clamp + responsive weight
                        (font-light on mobile for legibility, font-extralight
                        on lg+ for the editorial feel). Same treatment
                        every other description on the home page uses. */}
                    <HtmlText className="prose-p:!text-[clamp(0.875rem,0.5vw+0.75rem,1.125rem)] prose-p:!font-light prose-p:!leading-relaxed prose-p:!text-black-700 lg:prose-p:!font-extralight">
                      {question.description}
                    </HtmlText>
                  </AccordionPanel>
                )}
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </div>
    </section>
  );
};

export default FaqSection;
